// src/pages/InterviewAssistant/CompetencyInterview.tsx
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Target, BarChart2, Award, AlertCircle
} from 'lucide-react';
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip
} from 'recharts';

// ------------------- данные компетенций и вопросов -------------------
const competencies = [
  {
    title: 'Лидерство',
    description: 'Способность вести за собой, вдохновлять и направлять команду',
    questions: [
      { id: 1, text: 'Приведите пример, когда вам удалось мотивировать команду на достижение сложной цели.', options: [
        { value: 1, label: 'Не было такого опыта' }, { value: 2, label: 'Пытался, но без особого успеха' },
        { value: 3, label: 'Иногда получалось' }, { value: 4, label: 'Регулярно мотивирую команду' },
        { value: 5, label: 'Являюсь примером и вдохновителем' }
      ]},
      { id: 2, text: 'Как вы справляетесь с ситуацией, когда член команды не выполняет задачу?', options: [
        { value: 1, label: 'Перепоручаю другому' }, { value: 2, label: 'Критикую' },
        { value: 3, label: 'Провожу беседу и выясняю причины' }, { value: 4, label: 'Совместно ищем решение' },
        { value: 5, label: 'Постоянно развиваю подотчётность и поддержку' }
      ]},
    ],
  },
  {
    title: 'Коммуникация',
    description: 'Ясное и эффективное общение, умение слушать и доносить мысли',
    questions: [
      { id: 3, text: 'Опишите ситуацию, когда вам нужно было донести сложную информацию до незаинтересованной аудитории.', options: [
        { value: 1, label: 'Не сталкивался' }, { value: 2, label: 'Просто зачитал' },
        { value: 3, label: 'Использовал примеры' }, { value: 4, label: 'Адаптировал стиль и получил обратную связь' },
        { value: 5, label: 'Провёл интерактивную сессию с вовлечением' }
      ]},
      { id: 4, text: 'Как вы обычно даёте обратную связь коллеге?', options: [
        { value: 1, label: 'Избегаю' }, { value: 2, label: 'Прямо и критично' },
        { value: 3, label: 'Стараюсь сбалансировать' }, { value: 4, label: 'Использую метод "сэндвича"' },
        { value: 5, label: 'Регулярная конструктивная обратная связь с фокусом на развитие' }
      ]},
    ],
  },
  {
    title: 'Решение проблем',
    description: 'Аналитическое мышление, поиск решений и преодоление препятствий',
    questions: [
      { id: 5, text: 'Расскажите о самой сложной проблеме, которую вам удалось решить.', options: [
        { value: 1, label: 'Не решал сложных проблем' }, { value: 2, label: 'Решил с посторонней помощью' },
        { value: 3, label: 'Проанализировал и нашёл стандартное решение' }, { value: 4, label: 'Применил творческий подход' },
        { value: 5, label: 'Системно решил, внедрил превентивные меры' }
      ]},
      { id: 6, text: 'Как вы действуете, если сталкиваетесь с неопределённостью?', options: [
        { value: 1, label: 'Паникую' }, { value: 2, label: 'Жду указаний' },
        { value: 3, label: 'Собираю информацию' }, { value: 4, label: 'Принимаю решение на основе доступных данных' },
        { value: 5, label: 'Создаю структуру и управляю рисками' }
      ]},
    ],
  },
  {
    title: 'Работа в команде',
    description: 'Сотрудничество, поддержка коллег и достижение общих целей',
    questions: [
      { id: 7, text: 'Приведите пример успешного командного проекта, где ваш вклад был ключевым.', options: [
        { value: 1, label: 'Не участвовал' }, { value: 2, label: 'Выполнял свою часть' },
        { value: 3, label: 'Активно участвовал' }, { value: 4, label: 'Координировал работу' },
        { value: 5, label: 'Вывел команду на новый уровень эффективности' }
      ]},
      { id: 8, text: 'Как вы реагируете на конфликт внутри команды?', options: [
        { value: 1, label: 'Игнорирую' }, { value: 2, label: 'Принимаю сторону' },
        { value: 3, label: 'Выслушиваю обе стороны' }, { value: 4, label: 'Медиатор, ищу компромисс' },
        { value: 5, label: 'Превентивно создаю атмосферу доверия' }
      ]},
    ],
  },
  {
    title: 'Ориентация на результат',
    description: 'Нацеленность на достижение целей, высокая продуктивность',
    questions: [
      { id: 9, text: 'Как вы ставите себе цели и отслеживаете их выполнение?', options: [
        { value: 1, label: 'Не ставлю' }, { value: 2, label: 'Держу в уме' },
        { value: 3, label: 'Записываю и периодически проверяю' }, { value: 4, label: 'Использую методики SMART/OKR' },
        { value: 5, label: 'Система регулярного пересмотра целей с аналитикой' }
      ]},
      { id: 10, text: 'Расскажите о случае, когда вам удалось перевыполнить план.', options: [
        { value: 1, label: 'Не перевыполнял' }, { value: 2, label: 'Случайно' },
        { value: 3, label: 'За счёт дополнительных усилий' }, { value: 4, label: 'Оптимизировал процессы' },
        { value: 5, label: 'Внедрил инновацию, кратно повысив результат' }
      ]},
    ],
  },
  {
    title: 'Адаптивность',
    description: 'Гибкость в меняющихся условиях, быстрое освоение нового',
    questions: [
      { id: 11, text: 'Опишите ситуацию, когда вам пришлось быстро перестроиться из‑за внешних изменений.', options: [
        { value: 1, label: 'Не сталкивался' }, { value: 2, label: 'Сопротивлялся' },
        { value: 3, label: 'Принял, но с трудом' }, { value: 4, label: 'Быстро адаптировался' },
        { value: 5, label: 'Увидел возможности и возглавил изменения' }
      ]},
      { id: 12, text: 'Как вы осваиваете новый инструмент или технологию?', options: [
        { value: 1, label: 'Избегаю' }, { value: 2, label: 'Жду обучения' },
        { value: 3, label: 'Самостоятельно изучаю по необходимости' }, { value: 4, label: 'Активно экспериментирую' },
        { value: 5, label: 'Постоянно учусь и делюсь знаниями с командой' }
      ]},
    ],
  },
  {
    title: 'Клиентоориентированность',
    description: 'Понимание потребностей клиента, стремление к высокому сервису',
    questions: [
      { id: 13, text: 'Приведите пример, когда вы пошли навстречу клиенту в нестандартной ситуации.', options: [
        { value: 1, label: 'Отказал' }, { value: 2, label: 'Выполнил строго по регламенту' },
        { value: 3, label: 'Предложил альтернативу' }, { value: 4, label: 'Индивидуально решил проблему' },
        { value: 5, label: 'Превзошёл ожидания и получил благодарность' }
      ]},
      { id: 14, text: 'Как вы определяете истинную потребность клиента?', options: [
        { value: 1, label: 'Не задумываюсь' }, { value: 2, label: 'Слушаю запрос' },
        { value: 3, label: 'Задаю уточняющие вопросы' }, { value: 4, label: 'Анализирую контекст и предлагаю решение' },
        { value: 5, label: 'Выстраиваю долгосрочные партнёрские отношения' }
      ]},
    ],
  },
  {
    title: 'Стрессоустойчивость',
    description: 'Способность сохранять эффективность под давлением',
    questions: [
      { id: 15, text: 'Как вы действуете в условиях жёстких дедлайнов?', options: [
        { value: 1, label: 'Срываю сроки' }, { value: 2, label: 'Работаю, но качество страдает' },
        { value: 3, label: 'Приоритизирую и справляюсь' }, { value: 4, label: 'Сохраняю спокойствие и веду команду' },
        { value: 5, label: 'Использую стресс как драйвер для роста' }
      ]},
      { id: 16, text: 'Случалось ли вам принимать важное решение в кризисной ситуации?', options: [
        { value: 1, label: 'Нет' }, { value: 2, label: 'Да, но с ошибками' },
        { value: 3, label: 'Да, успешно' }, { value: 4, label: 'Неоднократно, с анализом рисков' },
        { value: 5, label: 'Регулярно, и это моя сильная сторона' }
      ]},
    ],
  },
];

const interpretation: Record<string, { low: string; medium: string; high: string }> = {
  Лидерство: {
    low: 'Рекомендуется развивать навыки делегирования и мотивации. Обратите внимание на тренинги по ситуационному лидерству.',
    medium: 'Хорошие задатки, но есть зоны роста. Попробуйте чаще брать на себя роль наставника.',
    high: 'Выраженные лидерские качества. Вы способны вести за собой людей и вдохновлять на достижение целей.'
  },
  Коммуникация: {
    low: 'Стоит работать над ясностью изложения и активным слушанием. Практикуйте публичные выступления.',
    medium: 'Вы умеете доносить мысли, но можно улучшить обратную связь и эмпатию.',
    high: 'Отличные коммуникативные навыки. Вы легко находите общий язык с любой аудиторией.'
  },
  'Решение проблем': {
    low: 'Развивайте аналитическое мышление и методики структурирования проблем (например, 5 Why).',
    medium: 'Вы способны решать задачи, но в сложных ситуациях может не хватать системности.',
    high: 'Сильный навык решения проблем. Вы видите корень проблемы и находите оптимальные решения.'
  },
  'Работа в команде': {
    low: 'Поработайте над коллаборацией. Участвуйте в командных проектах и учитесь слышать других.',
    medium: 'Вы командный игрок, но иногда можете замыкаться на своих задачах.',
    high: 'Высокая степень сотрудничества. Команда с вами работает эффективнее.'
  },
  'Ориентация на результат': {
    low: 'Установите чёткие цели и дедлайны. Используйте системы планирования.',
    medium: 'Вы нацелены на результат, но иногда отвлекаетесь на процесс.',
    high: 'Сильная результативность. Вы ставите амбициозные цели и достигаете их.'
  },
  Адаптивность: {
    low: 'Развивайте гибкость: пробуйте новое, выходите из зоны комфорта.',
    medium: 'Вы адаптируетесь, но не всегда быстро. Работайте над принятием изменений.',
    high: 'Вы легко приспосабливаетесь к переменам и используете их в своих интересах.'
  },
  Клиентоориентированность: {
    low: 'Обратите внимание на потребности клиентов. Спрашивайте обратную связь.',
    medium: 'Вы учитываете интересы клиента, но не всегда идёте навстречу.',
    high: 'Клиент для вас на первом месте. Вы создаёте выдающийся сервис.'
  },
  Стрессоустойчивость: {
    low: 'Работайте над управлением стрессом: дыхательные практики, тайм-менеджмент.',
    medium: 'Вы справляетесь, но длительное давление может снижать эффективность.',
    high: 'Вы сохраняете хладнокровие в любых ситуациях и помогаете другим.'
  },
};

// ------------------- компонент -------------------
const CompetencyInterview: React.FC = () => {
  const navigate = useNavigate();
  const [currentCompetencyIndex, setCurrentCompetencyIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [submitted, setSubmitted] = useState(false);

  const allQuestions = useMemo(() => competencies.flatMap(c => c.questions), []);

  const handleAnswer = (questionId: number, value: number) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const currentCompetency = competencies[currentCompetencyIndex];
  const currentQuestions = currentCompetency.questions;
  const allAnswered = allQuestions.every(q => answers[q.id] !== undefined);

  const goNext = () => {
    if (currentCompetencyIndex < competencies.length - 1) {
      setCurrentCompetencyIndex(prev => prev + 1);
    }
  };

  const goPrev = () => {
    if (currentCompetencyIndex > 0) {
      setCurrentCompetencyIndex(prev => prev - 1);
    }
  };

  const handleSubmit = () => setSubmitted(true);

  const scoresByCompetency = useMemo(() => {
    return competencies.map(comp => {
      const total = comp.questions.reduce((sum, q) => sum + (answers[q.id] || 0), 0);
      const max = comp.questions.length * 5;
      const percentage = Math.round((total / max) * 100);
      return { title: comp.title, percentage, total };
    });
  }, [answers]);

  const radarData = scoresByCompetency.map(s => ({
    competency: s.title,
    score: s.percentage,
    fullMark: 100,
  }));

  // ---------- страница результатов ----------
  if (submitted) {
    return (
      <div style={styles.container}>
        <div style={styles.bgBlur1} />
        <div style={styles.bgBlur2} />

        <button onClick={() => navigate('/app/interview-assistant')} style={styles.backButton}>
          <ArrowLeft size={18} />
          <span>К помощнику интервью</span>
        </button>

        <div style={styles.header}>
          <h1 style={styles.title}>Результаты интервью по компетенциям</h1>
        </div>

        <div style={styles.resultsGrid}>
          {/* Радар */}
          <div style={styles.resultCard}>
            <h3 style={styles.chartTitle}><BarChart2 size={18} /> Профиль компетенций</h3>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={radarData} outerRadius={100}>
                <PolarGrid strokeDasharray="3 3" />
                <PolarAngleAxis dataKey="competency" tick={{ fontSize: 11 }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} />
                <Radar isAnimationActive={false} name="Уровень" dataKey="score" stroke="#2575fc" fill="#2575fc" fillOpacity={0.2} />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          {/* Детализация */}
          <div style={styles.resultCard}>
            <h3 style={styles.chartTitle}><Award size={18} /> Детализация по компетенциям</h3>
            {scoresByCompetency.map(s => {
              const interp = interpretation[s.title];
              const level = s.percentage >= 70 ? 'high' : s.percentage >= 40 ? 'medium' : 'low';
              const color = level === 'high' ? '#2ecc71' : level === 'medium' ? '#f39c12' : '#e74c3c';
              return (
                <div key={s.title} style={styles.competencyRow}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <strong>{s.title}</strong>
                    <span style={{ color }}>{s.percentage}%</span>
                  </div>
                  <div style={styles.progressBar}>
                    <div style={{ ...styles.progressFill, width: `${s.percentage}%`, background: color }} />
                  </div>
                  <p style={styles.interpretationText}>
                    {level === 'high' && interp.high}
                    {level === 'medium' && interp.medium}
                    {level === 'low' && interp.low}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        <button
          onClick={() => { setSubmitted(false); setAnswers({}); setCurrentCompetencyIndex(0); }}
          style={styles.resetButton}
        >
          Пройти заново
        </button>
      </div>
    );
  }

  // ---------- вопросы ----------
  return (
    <div style={styles.container}>
      <div style={styles.bgBlur1} />
      <div style={styles.bgBlur2} />

      <button onClick={() => navigate('/app/interview-assistant')} style={styles.backButton}>
        <ArrowLeft size={18} />
        <span>К помощнику интервью</span>
      </button>

      <div style={styles.header}>
        <h1 style={styles.title}>Интервью по компетенциям</h1>
        <p style={styles.subtitle}>
          Оцените кандидата (или себя) по ключевым компетенциям. Выберите наиболее подходящий ответ для каждого вопроса.
        </p>
      </div>

      {/* Индикатор прогресса */}
      <div style={styles.progressIndicator}>
        {competencies.map((_, idx) => (
          <div
            key={idx}
            style={{
              ...styles.dot,
              background: idx <= currentCompetencyIndex ? '#2575fc' : '#e0e0e0',
            }}
          />
        ))}
      </div>

      <div style={styles.card}>
        <h2 style={styles.competencyTitle}>
          <Target size={20} /> {currentCompetency.title}
        </h2>
        <p style={styles.competencyDesc}>{currentCompetency.description}</p>

        {currentQuestions.map(q => (
          <div key={q.id} style={styles.questionBlock}>
            <p style={styles.questionText}>{q.text}</p>
            <div style={styles.optionsGrid}>
              {q.options.map(opt => (
                <button
                  key={opt.value}
                  style={{
                    ...styles.optionButton,
                    background: answers[q.id] === opt.value ? 'linear-gradient(135deg, #6a11cb, #2575fc)' : 'rgba(255,255,255,0.5)',
                    color: answers[q.id] === opt.value ? 'white' : '#1a1a2e',
                    border: answers[q.id] === opt.value ? 'none' : '1px solid rgba(0,0,0,0.1)',
                  }}
                  onClick={() => handleAnswer(q.id, opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        ))}

        <div style={styles.navButtons}>
          <button onClick={goPrev} disabled={currentCompetencyIndex === 0} style={styles.navButton}>
            Назад
          </button>
          <span style={{ color: '#666' }}>
            {currentCompetencyIndex + 1} / {competencies.length}
          </span>
          {currentCompetencyIndex < competencies.length - 1 ? (
            <button onClick={goNext} style={styles.navButton}>Далее</button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!allAnswered}
              style={{
                ...styles.navButton,
                background: allAnswered ? 'linear-gradient(135deg, #2ecc71, #27ae60)' : '#ccc',
                color: 'white',
                border: 'none',
              }}
            >
              Завершить и получить результат
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ------------------- стили -------------------
const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '80px 20px 20px 20px',
    background: 'linear-gradient(145deg, #f8fafd 0%, #e8f0fe 100%)',
    fontFamily: '"Inter", "Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
    position: 'relative',
    overflowX: 'clip',                     // вместо 'hidden', чтобы не создавать контекст скролла
    willChange: 'transform',              // ускоряет композитинг
    transform: 'translateZ(0)',           // создаёт отдельный слой

  },
  bgBlur1: {
    position: 'absolute', top: '-10%', left: '-5%', width: 600, height: 600,
    background: 'radial-gradient(circle, rgba(106,17,203,0.12) 0%, rgba(106,17,203,0) 70%)',
    borderRadius: '50%', filter: 'blur(60px)', zIndex: 0,
  },
  bgBlur2: {
    position: 'absolute', bottom: '-10%', right: '-5%', width: 700, height: 700,
    background: 'radial-gradient(circle, rgba(37,117,252,0.12) 0%, rgba(37,117,252,0) 70%)',
    borderRadius: '50%', filter: 'blur(60px)', zIndex: 0,
  },
  backButton: {
    position: 'absolute', top: 24, left: 24,
    background: 'rgba(255,255,255,0.6)',
    border: '1px solid rgba(0,0,0,0.06)',
    borderRadius: 40,
    padding: '10px 20px', fontSize: 15, fontWeight: 500, color: '#2575fc',
    cursor: 'pointer', transition: 'all 0.2s ease', zIndex: 10, display: 'inline-flex', alignItems: 'center', gap: 8,
  },
  header: { textAlign: 'center', marginBottom: 32, position: 'relative', zIndex: 1 },
  title: {
    fontSize: 36, fontWeight: 700, background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', margin: '0 0 8px',
  },
  subtitle: { fontSize: 16, color: '#666', margin: 0 },
  progressIndicator: { display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 24 },
  dot: { width: 12, height: 12, borderRadius: '50%', transition: 'background 0.3s' },
  card: {
    background: 'rgba(255,255,255,0.5)', borderRadius: 32,
    padding: '40px 32px', border: '1px solid rgba(0,0,0,0.06)',
    maxWidth: 800, margin: '0 auto', position: 'relative', zIndex: 1,
  },
  competencyTitle: { fontSize: 24, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  competencyDesc: { fontSize: 15, color: '#4a4a68', marginBottom: 28 },
  questionBlock: { marginBottom: 28 },
  questionText: { fontSize: 16, fontWeight: 500, marginBottom: 12, color: '#1a1a2e' },
  optionsGrid: { display: 'flex', flexDirection: 'column', gap: 10 },
  optionButton: {
    textAlign: 'left' as const, padding: '14px 20px', borderRadius: 16, cursor: 'pointer',
    transition: 'all 0.2s', fontSize: 14, border: '1px solid rgba(0,0,0,0.1)',
  },
  navButtons: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 32 },
  navButton: {
    padding: '10px 24px', borderRadius: 30, border: '1px solid #2575fc', background: 'transparent',
    color: '#2575fc', cursor: 'pointer', fontWeight: 600, transition: 'all 0.2s',
  },
  // Результаты
  resultsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: 28, maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 },
  resultCard: {
    background: 'rgba(255,255,255,0.5)', borderRadius: 32,
    padding: 32, border: '1px solid rgba(0,0,0,0.06)',
  },
  chartTitle: { fontSize: 20, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 },
  competencyRow: { marginBottom: 20 },
  progressBar: { height: 8, background: '#e0e0e0', borderRadius: 4, marginTop: 6, overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 4, transition: 'width 0.3s' },
  interpretationText: { fontSize: 13, color: '#555', marginTop: 6, lineHeight: 1.5 },
  resetButton: {
    marginTop: 40, display: 'block', marginLeft: 'auto', marginRight: 'auto',
    padding: '12px 28px', borderRadius: 30, background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    color: 'white', border: 'none', cursor: 'pointer', fontWeight: 600,
  },
};

export default CompetencyInterview;